import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { AppStore } from '@app/app.store';
import { DecodedDataValue } from '@app/features/decoded-data/models/decoded-data-value.model';
import { Device } from '@app/shared/models/device.interface';
import { Entity } from '@app/shared/models/models.interface';
import { Page } from '@app/shared/models/page.interface';
import { SaveResult } from '@app/shared/models/saveResult';
import { User } from '@app/shared/models/user.interface';
import { DataTablePageState } from '@components/data/data-table-new/directives/datatable-pagination.directive';
import { DataTableSortState } from '@components/data/data-table-new/directives/datatable-sort.directive';
import { Observable, of } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';
import { DecodedDataValueService } from '../decodeddata/decoded-data-value.service';
import { ErrorHandlerService } from '../error-handler/error-handler.service';

export interface DeviceActiveResponse {
  [id: string]: string;
}

export interface DeviceExportResponse {
  [id: string]: boolean;
}

//TODO: remove when merging with structures...
export interface Field extends Entity {
  name: string;
}

export interface Structure extends Entity {
  name: string;
  module: string;
  fields?: Field[];
}

export interface BadFile extends Entity {
  fileId?: string;
  url?: string;
  name?: string;
  creationDate?: string;
}

export interface DeviceWithUsersDto {
  device?: Device;
  users?: Array<string>;
}

export interface DeviceLastConnectionDto {
  timeStamp?: string;
  status?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  constructor(
    protected http: HttpClient,
    protected errorHandler: ErrorHandlerService,
    protected appStore: AppStore,
    private appConfigService: AppConfigService,
    protected decodedDataValueService: DecodedDataValueService
  ) {}

  getNew(): Observable<Device> {
    return of({ name: '', id: '' });
  }

  getById(id: string): Observable<Device> {
    return this.http.get<Device>(`${this.appConfigService.getApi()}/devices/${id}`);
  }

  getByIds(ids: string[]): Observable<Device[]> {
    return this.http.post<Device[]>(`${this.appConfigService.getApi()}/devices/ids`, { ids });
  }

  getByDevicename(devicename: string): Observable<Device> {
    return this.http.get<Device>(`${this.appConfigService.getApi()}/public/device/${devicename}`);
  }

  getAll(): Observable<Device[]> {
    return this.http.get<Device[]>(`${this.appConfigService.getApi()}/devices/all`);
  }

  getAllUsersForDevicePaginated(
    endpoint: string,
    page: DataTablePageState,
    sort: DataTableSortState,
    filter: string
  ): Observable<Page<User>> {
    let params = new HttpParams();
    params = params.append('filter', filter);
    params = params.append('page', page.index.toString());
    params = params.append('size', page.size.toString());
    params = params.append('sort', sort.column + ',' + sort.direction);

    return this.http.get<Page<User>>(this.appConfigService.getApi() + endpoint, {
      params
    });
  }

  getAllUsersForDevice(deviceId: string): Observable<User[]> {
    return this.http.get<User[]>(`${this.appConfigService.getApi()}/users/all/device/${deviceId}`);
  }

  // FIXME: DEPRECATED, follow the new format
  getAllPaginated(
    endpoint: string,
    page: DataTablePageState,
    sort: DataTableSortState,
    filter: string
  ): Observable<Page<Device>> {
    let params = new HttpParams();
    params = params.append('filter', filter);
    params = params.append('page', page.index.toString());
    params = params.append('size', page.size.toString());
    params = params.append('sort', sort.column + ',' + sort.direction);

    return this.http.get<Page<Device>>(this.appConfigService.getApi() + endpoint, {
      params
    });
  }

  save(dev: Device, users: User[]): Observable<SaveResult> {
    var uds: Array<string> = [];
    users.forEach(u => {
      uds.push(u.id);
    });
    var dto: DeviceWithUsersDto = {
      device: dev,
      users: uds
    };
    return dev.id != null ? this.update(dev.id, dto) : this.create(dto);
  }

  create(deviceWithUsersDto: DeviceWithUsersDto): Observable<SaveResult> {
    return this.http.put<Device>(`${this.appConfigService.getApi()}/devices`, deviceWithUsersDto);
  }

  update(id: string, deviceWithUsersDto: DeviceWithUsersDto): Observable<SaveResult> {
    return this.http.put<SaveResult>(`${this.appConfigService.getApi()}/devices/${id}`, deviceWithUsersDto);
  }

  changeState(selection: string[], active: boolean): Observable<DeviceActiveResponse> {
    if (active) {
      return this.http.post<any>(`${this.appConfigService.getApi()}/devices/activateList`, selection);
    } else {
      return this.http.post<any>(`${this.appConfigService.getApi()}/devices/deactivateList`, selection);
    }
  }

  /**
   * Generate a var load for a list of devices
   *
   * @param selection the devices
   * @return an Observable
   */
  variationLoad(selection: string[]): Observable<any> {
    return this.http.post<DeviceExportResponse>(`${this.appConfigService.getApi()}/dist/exportgroup/`, selection);
  }

  /**
   * Generate initial load for a list of devices
   *
   * @param selection the devices
   * @return an Observable
   */
  initialLoad(selection: string[]): Observable<any> {
    return this.http.post<DeviceExportResponse>(`${this.appConfigService.getApi()}/dist/exportgroupfull/`, selection);
  }

  /**
   * Generate table load for a list of devices
   *
   * @param selection the devices
   * @param tables the tables
   * @return an Observable
   */
  tableLoad(selection: string[], tables: any[]): Observable<any> {
    var tbIds = tables.map(t => t.id); //Map to IDs as expected by the endpoint
    return this.http.post<DeviceExportResponse>(`${this.appConfigService.getApi()}/dist/exportgroupTables/`, {
      ids: selection,
      tables: tbIds
    });
  }

  /**
   * Download bad files
   *
   * @param selection selected files (file ids)
   * @return an Observable
   */
  downloadBadFiles(selection: string[]): Observable<any> {
    return this.http.post(`${this.appConfigService.getApi()}/dist/downloadBadFiles/`, selection, {
      responseType: 'blob'
    });
  }

  /**
   * Delete bad files
   *
   * @param selection selected files (ids)
   * @return an Observable
   */
  deleteBadFiles(selection: string[]): Observable<any> {
    return this.http.post(`${this.appConfigService.getApi()}/dist/deleteBadFiles/`, selection);
  }

  /**
   * Import file for a given device
   *
   * @param device the device
   * @param file file upload instance
   * @return an Observable
   */
  importFile(device: Device, file?: any): Observable<any> {
    return this.http.post(`${this.appConfigService.getApi()}/dist/importFile/`, {
      deviceId: device.id,
      fileId: file.id
    });
  }

  /**
   * Get all the available device types
   *
   * @return an Observable
   */
  getAllDeviceTypes(): Observable<Array<DecodedDataValue>> {
    return this.decodedDataValueService.getAllByTableName('DCT_DEVICE_TYPES');
  }

  /**
   * Download logs for a list of devices
   *
   * @param ids the list of devices
   * @return an Observable
   */
  downloadLog(ids: string[]): Observable<any> {
    return this.http
      .post(`${this.appConfigService.getApi()}/dist/downloadLogDevices`, ids, { responseType: 'blob' })
      .pipe(take(1))
      .pipe(
        catchError(this.errorHandler.handleHttpError),
        finalize(() => {})
      );
  }

  /**
   * Download the all the records for a given device(s)
   *
   * @param id the device ids
   * @return an Observable
   */
  downloadAllRecords(ids: string[]): Observable<any> {
    return this.http
      .post(`${this.appConfigService.getApi()}/dist/downloadAllRecords`, ids, { responseType: 'blob' })
      .pipe(take(1))
      .pipe(
        catchError(this.errorHandler.handleHttpError),
        finalize(() => {})
      );
  }

  /**
   * Get the last connection by device
   */
  getLastConnection(id: string): Observable<DeviceLastConnectionDto> {
    return this.http.get<DeviceLastConnectionDto>(`${this.appConfigService.getApi()}/devices/lastConnection/${id}`);
  }
}
